import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useStatusOverlay } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'

export default function useReactQuery({
  queryFn,
  staleTime = 1200000,
  enabled = true,
  queryKey,
  showOnSuccessMessage = false,
  onSuccessMessage,
  // Optional string or (error) => string overriding the generic non-5xx overlay
  // message with something specific to the failed request
  onErrorMessage,
  // Set false when the caller already renders its own error state for this query
  // (a full-screen ErrorFallback, say) - otherwise the overlay toast fires on top
  // of it and the user gets two error messages for one failure. Suppresses only
  // the toast, for both 5xx and non-5xx; onError still runs either way.
  showErrorOverlay = true,
  refetchOnWindowFocus = false, // stops refetching when a user focuses on a different window & comes back (bit of a silly default behavior)
  onSuccess = () => {},
  onError = () => {},
  nextPrefetch = null,
  select = undefined,
  keepPreviousData = false,
  placeholderData = undefined,
  queryRetry = undefined,
  queryCacheTime = undefined,
  // Polling opt-in. Callers pass react-query's function form to stop polling on a
  // terminal state; note that a polling caller almost always wants staleTime: 0,
  // since this hook's 20-minute default would otherwise serve cache instead of refetching.
  refetchInterval = undefined,
}) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  onSuccessMessage = onSuccessMessage ?? t('success', 'Success!')
  const showStatusOverlay = useStatusOverlay()

  const query = useQuery({
    queryKey,
    queryFn,
    staleTime,
    enabled,
    refetchOnWindowFocus,
    select,
    keepPreviousData,
    placeholderData,
    ...(queryRetry === undefined ? {} : { retry: queryRetry }),
    ...(queryCacheTime === undefined ? {} : { cacheTime: queryCacheTime }),
    ...(refetchInterval === undefined ? {} : { refetchInterval }),
    onSuccess: (data) => {
      if (showOnSuccessMessage) {
        showStatusOverlay({ message: onSuccessMessage, type: 'success' })
      }
      if (nextPrefetch && 'requestIdleCallback' in window) {
        requestIdleCallback(() => {
          queryClient.prefetchQuery(nextPrefetch.queryKey, nextPrefetch.queryFn)
        })
      }
      onSuccess(data)
    },
    onError: (error) => {
      const errorCode = error?.response?.status
      if (showErrorOverlay) {
        if (errorCode >= 500) {
          showStatusOverlay({
            message: t('error.server', 'The server encountered an error and has been reported. Try again.'),
            type: 'error',
          })
        } else {
          const specificMessage = typeof onErrorMessage === 'function' ? onErrorMessage(error) : onErrorMessage
          showStatusOverlay({
            // Only honor string overrides; anything else falls back to the generic message
            message:
              typeof specificMessage === 'string' && specificMessage
                ? specificMessage
                : t('error.generic', 'Oops. Something went wrong.'),
            type: 'error',
          })
        }
      }
      // Runs for every failure, 5xx included. It previously fired only on the
      // non-5xx path, so a caller's own error handling was skipped for exactly
      // the most severe failures. Callers that pass onError (aam2's
      // useBulkFactsForCategory, which logs to Splunk before forwarding) now
      // also hear about 5xx responses - a deliberate behavior change.
      onError(error)
    },
  })

  return { query, data: query?.data?.data }
}
